import { useState, useEffect, useRef } from "react";
import {
  Autocomplete,
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  Select,
  Menu,
  MenuItem,
  Grid,
  Tooltip,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Picker from "emoji-picker-react";

import { readRemoteFile } from "react-papaparse";
import { CloudUpload } from "@mui/icons-material";

// Components
import { useNotification } from "../utils/Notification";
import { getUtcTimestamp, trackEvent } from "../utils/Utils";
import Plans from "../modals/Plans";
import ConfirmationModal from "../utils/ConfirmationModal";

// Context
import { useAuth } from "../../contexts/AuthContext";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

// Supabase
import supabase from "../../supabaseClient";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "80%", md: "60%", lg: "50%" },
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "24px",
  boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  overflowY: "auto",
  p: { xs: 3, md: 5 },
};

const messageToOptions = ["contact", "me", "manual", "group", "bulk"];

const messageFrequencyOptions = [
  "once",
  "daily",
  "weekly",
  "biweekly",
  "monthly",
  "bimester",
  "trimester",
  "quarter",
  "semester",
  "yearly",
];

export default function Message({
  open,
  onClose,
  message,
  readOnly,
  showButton,
  isJoyrideRunning = false,
}) {
  const [messageTo, setMessageTo] = useState("");
  const [contacts, setContacts] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [manualName, setManualName] = useState("");
  const [countryCodes, setCountryCodes] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState(null);
  const [manualPhone, setManualPhone] = useState("");
  const [messageText, setMessageText] = useState("");
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [labels, setLabels] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [labelToDelete, setLabelToDelete] = useState(null);
  const [messageFrequency, setMessageFrequency] = useState("once");
  const [firstMessageOn, setFirstMessageOn] = useState("");
  const [lastMessageOn, setLastMessageOn] = useState("");
  const [newLabelName, setNewLabelName] = useState("");
  const [bulkContacts, setBulkContacts] = useState(null);
  const [isCloning, setIsCloning] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const fileInputRef = useRef(null);
  const [plansModalOpen, setPlansModalOpen] = useState(false);
  const [groupWarningModalOpen, setGroupWarningModalOpen] = useState(false);
  const [deleteLabelModalOpen, setDeleteLabelModalOpen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [planData, setPlanData] = useState([]);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const translatedMessage = (
    <Trans
      i18nKey="message.whatsAppBlockRiskDescription"
      components={{ 1: <br /> }}
    />
  );

  // Variables Menu State
  const [contactVariables, setContactVariables] = useState([
    "first_name",
    "last_name",
    "nickname",
    "birthday",
    "countrycode_id",
    "phone",
  ]);
  const [bulkVariables, setBulkVariables] = useState([]);
  const [profileVariables, setProfileVariables] = useState([
    "first_name",
    "last_name",
    "nickname",
    "countrycode_id",
    "phone",
  ]);
  const koubiVariables = ["day", "month", "year", "date"];
  const [showVariableMenu, setShowVariableMenu] = useState(false);
  const [showContactVariables, setShowContactVariables] = useState(false);
  const [showProfileVariables, setShowProfileVariables] = useState(false);
  const [showKoubiVariables, setShowKoubiVariables] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const contentRef = useRef(null);
  const [tooltipOpen1, setTooltipOpen1] = useState(false);
  const [tooltipOpen2, setTooltipOpen2] = useState(false);

  // Context
  const { profileId } = useAuth();
  const { showNotification } = useNotification();

  useEffect(() => {
    if (isJoyrideRunning && open) {
      // Prefill fields when the modal is open during the tutorial
      setMessageTo("contact");
      setMessageText(t("messages.tutorial.messageDefault"));
    }
  }, [isJoyrideRunning, open]);

  useEffect(() => {
    const fetchContacts = async () => {
      let allContacts = [];
      let offset = 0;
      const pageSize = 1000;

      while (true) {
        const { data, error } = await supabase
          .from("contacts")
          .select("id, first_name, last_name")
          .order("first_name", { ascending: true })
          .range(offset, offset + pageSize - 1);

        if (error) {
          showNotification(t("message.errorFetchingContacts"), "error");
          break;
        }

        if (data && data.length > 0) {
          allContacts = [...allContacts, ...data];
        }

        if (!data || data.length < pageSize) {
          break;
        }

        offset += pageSize;
      }

      setContacts(allContacts);
    };

    const fetchGroups = async () => {
      const { data, error } = await supabase.from("groups").select("id, name");

      if (error) {
        showNotification(t("message.errorFetchingGroups"), "error");
      } else {
        setGroups(data);
      }
    };

    const fetchCountryCodes = async () => {
      const { data, error } = await supabase
        .from("countrycodes")
        .select("*")
        .order("country_iso", { ascending: true });

      if (error) {
        showNotification(t("message.errorFetchingCountryCodes"), "error");
      } else {
        setCountryCodes(data);
      }
    };

    const fetchTemplates = async () => {
      const { data, error } = await supabase
        .from("templates")
        .select("id, name, file_id, content");

      if (error) {
        showNotification(t("message.errorFetchingTemplates"), "error");
      } else {
        setTemplates(data);
      }
    };

    const fetchLabels = async () => {
      const { data, error } = await supabase
        .from("labels")
        .select("id, name")
        .eq("role_access", "{}");

      if (error) {
        showNotification(t("message.errorFetchingLabels"), "error");
      } else {
        setLabels(data);
      }
    };

    const fetchFiles = async () => {
      const { data, error } = await supabase
        .from("files")
        .select("id, name")
        .eq("role_access", "{}");

      if (error) {
        showNotification(t("message.errorFetchingFiles"), "error");
      } else {
        setFiles(data);
      }
    };

    const fetchContactVariables = async () => {
      const { data, error } = await supabase.from("variables").select("key");
      if (error) {
        showNotification(t("message.errorFetchingContactVariables"), "error");
      } else {
        const variableKeys = data ? data.map((item) => item.key) : [];
        const allVariableKeys = [
          ...contactVariables, // Default variables
          ...variableKeys, // Fetched variables
        ];
        setContactVariables(allVariableKeys);
      }
    };

    const fetchProfileVariables = async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("variables");
      if (error) {
        showNotification(t("message.errorFetchingProfileVariables"), "error");
      } else {
        const variableKeys =
          data && data[0] && data[0].variables
            ? Object.keys(data[0].variables)
            : [];
        const allVariableKeys = [
          ...profileVariables, // Default variables
          ...variableKeys, // Fetched variables
        ];
        setProfileVariables(allVariableKeys);
      }
    };

    const fetchPlanData = async () => {
      const { data, error } = await supabase
        .from("teams")
        .select("plan(group_messages, recurring_messages)")
        .single();

      if (!error) {
        setPlanData(data.plan);
      }
    };

    fetchTemplates();
    fetchContacts();
    fetchGroups();
    fetchCountryCodes();
    fetchLabels();
    fetchFiles();
    fetchContactVariables();
    fetchProfileVariables();
    fetchPlanData();
  }, [plansModalOpen]);

  useEffect(() => {
    // Prefill form fields with message data if available
    // Set first message on to now, but next rounded to 10 minutes.
    setFirstMessageOn(getNextRoundedTime());
    if (message) {
      setMessageTo(message.type);
      setSelectedContact(message.contact_id);
      setSelectedGroup(message.group_id);
      setManualName(message.name);
      setSelectedCountryCode(message.countrycode_id);
      setManualPhone(message.phone);
      setMessageText(message.message);
      setSelectedFile(message.file_id);
      setSelectedLabel(message.label_id);
      setMessageFrequency(message.frequency);
      setFirstMessageOn(message.next_at);
      setLastMessageOn(message.last_at);
      setBulkContacts(message.bulk_contacts);
    }

    // Set readOnly state based on prop
    setIsReadOnly(readOnly);
  }, [message, readOnly]);

  function getNextRoundedTime() {
    const now = new Date();
    const minutes = now.getMinutes();

    // Calculate the next multiple of 10
    const roundedMinutes = Math.ceil((minutes + 1) / 10) * 10;

    // If minutes go beyond 60, adjust the hour
    if (roundedMinutes === 60) {
      now.setHours(now.getHours() + 1);
      now.setMinutes(0);
    } else {
      now.setMinutes(roundedMinutes);
    }

    now.setSeconds(0);
    now.setMilliseconds(0);

    return now;
  }

  const handleChange = (field, value) => {
    switch (field) {
      case "messageTo":
        if (value === "group" || value === "bulk") {
          if (!planData.group_messages) {
            showNotification(t("message.groupMessagesNotSupported"), "warning");
            setPlansModalOpen(true);
            setMessageTo("");
            break;
          } else {
            setGroupWarningModalOpen(true);
          }
        }
        setMessageTo(value);
        setSelectedContact(null);
        setSelectedGroup(null);
        setManualName("");
        setSelectedCountryCode(null);
        setManualPhone("");
        setBulkContacts(null);
        break;
      case "contact":
        setSelectedContact(value);
        break;
      case "group":
        setSelectedGroup(value);
        break;
      case "manualName":
        setManualName(value);
        break;
      case "countryCode":
        setSelectedCountryCode(value);
        break;
      case "manualPhone":
        setManualPhone(value);
        break;
      case "messageText":
        setShowEmojiPicker(false);
        setMessageText(value);
        break;
      case "template":
        setSelectedTemplate(value);
        const template = templates.find((template) => template.id === value);
        setMessageText(template.content);
        if (template?.file_id) {
          setSelectedFile(template.file_id);
        }
        break;
      case "file":
        setSelectedFile(value);
        break;
      case "label":
        setSelectedLabel(value);
        break;
      case "frequency":
        if (!planData.recurring_messages && value !== "once") {
          showNotification(
            t("message.recurringMessagesNotSupported"),
            "warning"
          );
          setMessageFrequency("");
          setPlansModalOpen(true);
        } else {
          setMessageFrequency(value);
        }
        break;
      case "firstMessageOn":
        setFirstMessageOn(value);
        break;
      case "lastMessageOn":
        setLastMessageOn(value);
        break;
      default:
        break;
    }
  };

  const handleSubmit = async (e) => {
    trackEvent("message-form_submit");
    e.preventDefault();

    if (isReadOnly) {
      setIsReadOnly(false);
      setIsCloning(true);
      return;
    }

    if (messageTo === "contact" && selectedContact === null) {
      showNotification(t("message.emptyFields"), "warning");
      return;
    }

    const now = new Date();
    if (getUtcTimestamp(firstMessageOn) < getUtcTimestamp(now)) {
      // Means that they are setting a first message on in the past, so it's not possible
      showNotification(t("message.firstMessageDateInPast"), "warning");
      return;
    }

    if (getUtcTimestamp(lastMessageOn) < getUtcTimestamp(firstMessageOn)) {
      // Means that they are setting a last message on before the first message, which is not allowed
      showNotification(t("message.lastMessageDateInPast"), "warning");
      return;
    }

    try {
      const messageData = {
        to: messageTo,
        profile_id: profileId,
        file_id: selectedFile || null,
        label_id: selectedLabel,
        message_frequency: messageFrequency,
        scheduled_timestamp: getUtcTimestamp(firstMessageOn),
        end_timestamp: getUtcTimestamp(lastMessageOn),
        bulk_contacts_sent: null,
        message: messageText,
      };

      if (messageTo === "contact") {
        messageData.contact_id = selectedContact;
      } else if (messageTo === "group") {
        messageData.group_id = selectedGroup;
      } else if (messageTo === "manual") {
        messageData.name = manualName;
        messageData.countrycode_id = selectedCountryCode;
        messageData.phone = manualPhone;
      } else if (messageTo === "bulk") {
        messageData.name = manualName;
        messageData.bulk_contacts = bulkContacts;
      }

      // Update existing message if not cloning
      if (message && !isCloning) {
        const { error } = await supabase
          .from("messages")
          .update(messageData)
          .eq("id", message.id);

        if (error) {
          throw error;
        }
      } else {
        // Insert new message
        const { error } = await supabase.from("messages").insert([messageData]);

        if (error) {
          throw error;
        }
      }
      showNotification(
        t(
          `message.${isCloning
            ? "clonedMessage"
            : message
              ? "updatedMessage"
              : "scheduledMessage"
          }`
        ),
        "success"
      );
      resetValues();
    } catch (error) {
      showNotification(
        t("message.errorSendingMessage", { error: error.message })
      );
    }
  };

  const resetValues = () => {
    setMessageTo("");
    setSelectedContact(null);
    setSelectedGroup(null);
    setManualName("");
    setSelectedCountryCode(null);
    setManualPhone("");
    setMessageText("");
    setSelectedTemplate(null);
    setSelectedFile(null);
    setSelectedLabel(null);
    setMessageFrequency("once");
    setFirstMessageOn("");
    setLastMessageOn("");
    setNewLabelName("");
    setBulkContacts(null);
    setIsCloning(false);
    setShowContactVariables(false);
    setShowProfileVariables(false);
    setShowVariableMenu(false);
    setShowKoubiVariables(false);
    setShowEmojiPicker(false);
    setBulkVariables([]);
    setFirstMessageOn(getNextRoundedTime());

    onClose(); // Close the modal after reset
  };

  const handleAddNewLabel = async () => {
    trackEvent("message-new_label");
    if (newLabelName) {
      try {
        const { data, error } = await supabase
          .from("labels")
          .insert([
            {
              name: newLabelName,
              profile_id: profileId,
            },
          ])
          .select();

        if (error) {
          throw error;
        }

        setLabels([...labels, { id: data[0].id, name: newLabelName }]);
        setSelectedLabel(data[0].id);
        setNewLabelName("");
      } catch (error) {
        showNotification(
          t("message.errorAddingLabel", { errorMessage: error.message })
        );
      }
    }
  };

  const handleDeleteLabel = (e, id) => {
    e.stopPropagation();
    setDeleteLabelModalOpen(true);
    setLabelToDelete(id);
  };

  const handleDeleteLabelConfirmation = async () => {
    setDeleteLabelModalOpen(false);
    await supabase.from("labels").delete().eq("id", labelToDelete);
    setSelectedLabel(null);
    setLabels(() => labels.filter((label) => label.id !== labelToDelete));
    setLabelToDelete(null);
  };

  const handleBulkContactsDrop = (e) => {
    trackEvent("message-bulk_contacts_drop");
    e.preventDefault();
    processFile(e.dataTransfer.files[0]);
  };

  const handleBulkContactsChange = (e) => {
    trackEvent("message-bulk_contacts_change");
    processFile(e.target.files[0]);
  };

  const processFile = (file) => {
    if (file) {
      readRemoteFile(file, {
        header: true,
        complete: (results) => {
          const headers = Object.keys(results.data[0]);

          if (
            !headers.includes("countrycode_id") ||
            !headers.includes("phone")
          ) {
            showNotification(t("message.missingHeaders"), "error");
            return;
          }

          setBulkVariables(headers);
          const formattedContacts = results.data.map((row) => {
            const contactData = {};
            Object.keys(row).forEach((header) => {
              let value = row[header];
              if (header === "phone") {
                value = value.replace(/[^0-9]/g, "");
              } else if (header === "countrycode_id") {
                value =
                  countryCodes.find(
                    (row) => row.country_iso === value.toUpperCase()
                  )?.country_code || "521";
              }
              contactData[header] = value;
            });
            return contactData;
          });

          setBulkContacts(formattedContacts);
        },
      });
    }
  };

  const handleBoxClick = () => {
    trackEvent("message-import_box_clicked");
    fileInputRef.current.click();
  };

  // Handle variables menu:
  const handleContentChange = (e) => {
    const content = e.target.value;
    const cursorPosition = e.target.selectionStart;

    handleChange("messageText", content);

    if (content[cursorPosition - 1] === "@") {
      trackEvent("message-variables_menu_opened");
      const rect = contentRef.current.getBoundingClientRect();
      setMenuPosition({ x: rect.left, y: rect.bottom });
      setShowVariableMenu(true);
    } else {
      setShowVariableMenu(false);
      setShowContactVariables(false);
      setShowProfileVariables(false);
      setShowKoubiVariables(false);
    }
  };
  const handleVariableSelect = (variable, type) => {
    trackEvent("message-variable_select", { variable, type });
    const currentContent = messageText;
    const cursorPosition = contentRef.current.selectionStart;
    const newContent = `${currentContent.substring(
      0,
      cursorPosition - 1
    )}%${type}.${variable}%${currentContent.substring(cursorPosition)}`;
    handleChange("messageText", newContent);
    setShowContactVariables(false);
    setShowProfileVariables(false);
    setShowKoubiVariables(false);
    setShowVariableMenu(false);
  };

  const handleClosePlansModal = () => {
    trackEvent("message-plans_modal_closed");
    setPlansModalOpen(false);
  };

  const handleNewItemClick = (destination) => {
    trackEvent("message-new_item_click", { destination });
    navigate(`/dashboard/${destination}`);
  };

  const handleEmojiClick = (event) => {
    const cursorPosition = contentRef.current.selectionStart;
    const currentContent = messageText;
    const newContent =
      currentContent.substring(0, cursorPosition) +
      event.emoji +
      currentContent.substring(cursorPosition);
    handleChange("messageText", newContent);
  };

  return (
    <Box>
      <Modal open={open} onClose={resetValues}>
        <Box sx={style} className="message-modal-content">
          <form onSubmit={handleSubmit}>
            {/* Form Fields */}
            <Grid container spacing={2}>
              {/* To Field */}
              <Grid item xs={12}>
                <FormControl fullWidth disabled={isReadOnly}>
                  <InputLabel id="message-to-label">
                    {t("message.to")}
                  </InputLabel>
                  <Select
                    labelId="message-to-label"
                    id="message-to"
                    value={messageTo}
                    label="To"
                    onChange={(e) => handleChange("messageTo", e.target.value)}
                    required
                  >
                    {messageToOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {t(`message.${option}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Contact Selection */}
              {messageTo === "contact" && (
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={isReadOnly}>
                    <Autocomplete
                      id="contact-autocomplete"
                      options={[
                        ...contacts,
                        {
                          // Add "+ New" option
                          id: "new",
                          first_name: "+",
                          last_name: t("message.new"),
                        },
                      ]}
                      getOptionLabel={(option) =>
                        `${option.first_name} ${option.last_name}`
                      }
                      value={
                        contacts.find(
                          (contact) => contact.id === selectedContact
                        ) || null
                      }
                      onChange={(e, newValue) => {
                        if (newValue) {
                          if (newValue.id === "new") {
                            handleNewItemClick("contacts");
                          } else {
                            handleChange("contact", newValue.id);
                          }
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t("message.selectContact")}
                          placeholder={t("message.searchContact")}
                          className="message-modal-contact-list"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {`${option.first_name} ${option.last_name}`}
                        </li>
                      )}
                      noOptionsText={t("message.noContacts")}
                    />
                  </FormControl>
                </Grid>
              )}

              {/* Group Selection */}
              {messageTo === "group" && (
                <Grid item xs={12}>
                  <FormControl fullWidth disabled={isReadOnly}>
                    <InputLabel id="group-select-label">
                      {t("message.selectGroup")}
                    </InputLabel>
                    <Select
                      labelId="group-select-label"
                      id="group-select"
                      value={selectedGroup || ""}
                      label="Select Group"
                      onChange={(e) => handleChange("group", e.target.value)}
                      required
                    >
                      {groups.map((group) => (
                        <MenuItem key={group.id} value={group.id}>
                          {group.name}
                        </MenuItem>
                      ))}
                      <MenuItem
                        onClick={() =>
                          handleNewItemClick("contacts?tab=groups")
                        }
                      >
                        + {t("message.new")}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* Manual Input Section */}
              {messageTo === "manual" && (
                <>
                  {/* Name Field */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t("message.name")}
                      value={manualName}
                      onChange={(e) =>
                        handleChange("manualName", e.target.value)
                      }
                      required
                      disabled={isReadOnly}
                    />
                  </Grid>

                  {/* Country Code Selection */}
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth disabled={isReadOnly}>
                      <InputLabel id="country-code-label">
                        {t("message.countryCode")}
                      </InputLabel>
                      <Select
                        labelId="country-code-label"
                        id="country-code"
                        value={selectedCountryCode}
                        label="Country Code"
                        onChange={(e) =>
                          handleChange("countryCode", e.target.value)
                        }
                        required
                      >
                        {countryCodes.map((code) => (
                          <MenuItem
                            key={code.country_iso}
                            value={code.country_iso}
                          >
                            {`${code.country_iso} (${code.country_code})`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  {/* Phone Number Field */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label={t("message.phone")}
                      type="number"
                      value={manualPhone}
                      onChange={(e) =>
                        handleChange("manualPhone", e.target.value)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                          e.preventDefault();
                        }
                      }}
                      required
                      disabled={isReadOnly}
                    />
                  </Grid>
                </>
              )}

              {/* Bulk Input Section */}
              {messageTo === "bulk" && (
                <>
                  {/* Campaign Name Field */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t("message.campaignName")}
                      value={manualName}
                      onChange={(e) =>
                        handleChange("manualName", e.target.value)
                      }
                      required
                      disabled={isReadOnly}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    {/* Import Box */}
                    {!bulkContacts ? (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="h6">
                            {t("message.importContacts")}{" "}
                            <a
                              href="https://docs.google.com/spreadsheets/d/1yq9sFN-0DwQD_DqdIlbiwNVWHtMuCjMDNMyrOJhWc3o/edit?gid=0#gid=0"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t("message.template")}
                            </a>
                            , {t("message.watchTutorial")}{" "}
                            <a
                              href={t("message.bulkMessageTutorialUrl")}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {t("message.tutorial")}
                            </a>
                            .
                          </Typography>
                        </Grid>
                        <Box
                          onClick={handleBoxClick}
                          onDrop={handleBulkContactsDrop}
                          onDragOver={(e) => e.preventDefault()}
                          sx={{
                            border: "2px dashed #ccc",
                            p: 4,
                            textAlign: "center",
                            cursor: "pointer",
                            borderRadius: 2,
                            backgroundColor: "action.hover",
                            "&:hover": {
                              backgroundColor: "action.selected",
                              borderColor: "primary.main",
                            },
                          }}
                          mb="14px"
                        >
                          <CloudUpload
                            sx={{ fontSize: 40, color: "primary.main" }}
                          />
                          <Typography variant="h6" mb={2}>
                            {t("message.dragAndDrop")}
                          </Typography>
                        </Box>
                      </>
                    ) : null}
                    {bulkContacts && (
                      <Typography>
                        {bulkContacts.length} {t("message.contactsImported")}
                      </Typography>
                    )}
                  </Grid>
                </>
              )}
              {message?.message_status === "partial_sent" &&
                message?.bulk_contacts_sent?.length > 0 && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t("message.bulkContactsSent")}
                      value={message.bulk_contacts_sent}
                      multiline
                      rows={4}
                      disabled
                    />
                  </Grid>
                )}
              {message?.message_status === "partial_sent" &&
                message?.bulk_contacts_failed?.length > 0 && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label={t("message.bulkContactsFailed")}
                      value={message.bulk_contacts_failed}
                      multiline
                      rows={4}
                      disabled
                    />
                  </Grid>
                )}
              <Grid item xs={12}>
                <FormControl fullWidth disabled={isReadOnly}>
                  <InputLabel id="template-select-label">
                    {t("message.selectTemplate")}
                  </InputLabel>
                  <Select
                    labelId="template-select-label"
                    id="template-select"
                    value={selectedTemplate || ""}
                    label="Select Template"
                    onChange={(e) => handleChange("template", e.target.value)}
                  >
                    {templates.map((template) => (
                      <MenuItem key={template.id} value={template.id}>
                        {template.name}
                      </MenuItem>
                    ))}
                    <MenuItem onClick={() => handleNewItemClick("templates")}>
                      + {t("message.new")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">
                  {t("message.variablesTutorial")}
                  <a
                    href={t("message.variablesTutorialUrl")}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("message.tutorial")}
                  </a>
                  .{/* Tooltip Icon next to the text */}
                  <Tooltip
                    title={
                      <h2 style={{ fontSize: "13px" }}>
                        {t("message.variablesDescription")}
                      </h2>
                    }
                    open={tooltipOpen1}
                    onClose={() => setTooltipOpen1(false)}
                    disableHoverListener
                  >
                    <IconButton
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={() => setTooltipOpen1(true)}
                    >
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ position: "relative" }}>
                  <TextField
                    fullWidth
                    label={t("message.messageText")}
                    disabled={isReadOnly}
                    multiline
                    rows={4}
                    value={messageText}
                    inputRef={contentRef}
                    onChange={handleContentChange}
                    required
                    InputProps={{
                      // Padding from text inside TextField
                      sx: { paddingRight: "28px" },
                    }}
                  />
                  <IconButton
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    sx={{
                      position: "absolute",
                      top: -2,
                      right: -1,
                      color: "black",
                    }}
                  >
                    <span
                      role="img"
                      aria-label="emoji"
                      style={{ fontSize: "1rem" }}
                    >
                      😊
                    </span>
                  </IconButton>
                  {showEmojiPicker && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 40,
                        right: 0,
                        zIndex: 1000,
                        border: "1px solid #ddd",
                        backgroundColor: "white",
                      }}
                    >
                      <Picker onEmojiClick={handleEmojiClick} />
                    </Box>
                  )}
                </Box>
                <Menu
                  open={showVariableMenu}
                  onClose={() => setShowVariableMenu(false)}
                  anchorReference="anchorPosition"
                  anchorPosition={{
                    top: menuPosition.y,
                    left: menuPosition.x,
                  }}
                >
                  <MenuItem onClick={() => setShowContactVariables(true)}>
                    {t("message.contact")}
                    <Menu
                      open={showContactVariables}
                      onClose={() => setShowContactVariables(false)}
                      anchorReference="anchorPosition"
                      anchorPosition={{
                        top: menuPosition.y,
                        left: menuPosition.x,
                      }}
                    >
                      {(bulkVariables.length
                        ? bulkVariables
                        : contactVariables
                      ).map((variable) => (
                        <MenuItem
                          key={variable}
                          onClick={() =>
                            handleVariableSelect(variable, "contact")
                          }
                        >
                          {variable}
                        </MenuItem>
                      ))}
                    </Menu>
                  </MenuItem>
                  <MenuItem onClick={() => setShowProfileVariables(true)}>
                    {t("message.profile")}
                    <Menu
                      open={showProfileVariables}
                      onClose={() => setShowProfileVariables(false)}
                      anchorReference="anchorPosition"
                      anchorPosition={{
                        top: menuPosition.y,
                        left: menuPosition.x,
                      }}
                    >
                      {profileVariables.map((variable) => (
                        <MenuItem
                          key={variable}
                          onClick={() =>
                            handleVariableSelect(variable, "profile")
                          }
                        >
                          {variable}
                        </MenuItem>
                      ))}
                    </Menu>
                  </MenuItem>
                  <MenuItem onClick={() => setShowKoubiVariables(true)}>
                    {t("message.koubi")}
                    <Menu
                      open={showKoubiVariables}
                      onClose={() => setShowKoubiVariables(false)}
                      anchorReference="anchorPosition"
                      anchorPosition={{
                        top: menuPosition.y,
                        left: menuPosition.x,
                      }}
                    >
                      {koubiVariables.map((variable) => (
                        <MenuItem
                          key={variable}
                          onClick={() =>
                            handleVariableSelect(variable, "koubi")
                          }
                        >
                          {variable}
                        </MenuItem>
                      ))}
                    </Menu>
                  </MenuItem>
                </Menu>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth disabled={isReadOnly}>
                  <InputLabel id="file-select-label">
                    {t("message.file")}
                  </InputLabel>
                  <Select
                    labelId="file-select-label"
                    id="file-select"
                    value={selectedFile || ""}
                    label="File"
                    onChange={(e) => handleChange("file", e.target.value)}
                  >
                    <MenuItem value="">{t("message.none")}</MenuItem>
                    {files.map((file) => (
                      <MenuItem key={file.id} value={file.id}>
                        {file.name}
                      </MenuItem>
                    ))}
                    <MenuItem onClick={() => handleNewItemClick("files")}>
                      + {t("message.new")}
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth disabled={isReadOnly}>
                  <InputLabel id="label-select-label">
                    {t("message.label")}
                  </InputLabel>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Select
                      labelId="label-select-label"
                      id="label-select"
                      value={selectedLabel || ""}
                      label="Label"
                      onChange={(e) => handleChange("label", e.target.value)}
                      renderValue={(selected) =>
                        labels.find((label) => label.id === selected)?.name ||
                        "None"
                      }
                      sx={{ flexGrow: 1 }}
                    >
                      <MenuItem value="">{t("message.none")}</MenuItem>
                      {labels.map((label) => (
                        <MenuItem key={label.id} value={label.id}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                          >
                            {label.name}
                            <IconButton
                              size="small"
                              onClick={(e) => handleDeleteLabel(e, label.id)}
                              sx={{ color: "error.main" }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </MenuItem>
                      ))}
                      <MenuItem value="+ New">+ {t("message.new")}</MenuItem>{" "}
                    </Select>

                    {/* Information Tooltip Icon */}
                    <Tooltip
                      title={
                        <h2 style={{ fontSize: "13px" }}>
                          {t("message.labelDescription")}
                        </h2>
                      }
                      open={tooltipOpen2}
                      onClose={() => setTooltipOpen2(false)}
                      disableHoverListener
                    >
                      <IconButton
                        size="small"
                        sx={{ ml: 1 }}
                        onClick={() => setTooltipOpen2(true)}
                      >
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </FormControl>
              </Grid>
              {selectedLabel === "+ New" && (
                <Grid item xs={12}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <TextField
                      label={t("message.newLabelName")}
                      value={newLabelName}
                      onChange={(e) => setNewLabelName(e.target.value)}
                      sx={{ mr: 2 }}
                    />
                    <Button
                      variant="contained"
                      onClick={handleAddNewLabel}
                      sx={{
                        borderRadius: "8px",
                        fontWeight: "bold",
                        background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
                        color: "#fff",
                        boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
                        "&:hover": { background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)" }
                      }}
                    >
                      {t("message.add")}
                    </Button>
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <FormControl fullWidth disabled={isReadOnly}>
                  <InputLabel id="frequency-select-label">
                    {t("message.frequency")}
                  </InputLabel>
                  <Select
                    labelId="frequency-select-label"
                    id="frequency-select"
                    value={messageFrequency}
                    label="Frequency"
                    onChange={(e) => handleChange("frequency", e.target.value)}
                    required
                  >
                    {messageFrequencyOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {t(`message.${option}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label={t("message.firstMessageOn")}
                    value={firstMessageOn ? dayjs(firstMessageOn) : null}
                    onChange={(newValue) => {
                      if (newValue && newValue.isValid()) {
                        handleChange("firstMessageOn", newValue.toISOString());
                      } else {
                        handleChange("firstMessageOn", newValue);
                      }
                    }}
                    inputFormat="YYYY-MM-DD, hh:mm A"
                    renderInput={(params) => (
                      <TextField {...params} fullWidth required />
                    )}
                    disabled={isReadOnly}
                    minutesStep={10}
                  />
                </LocalizationProvider>
              </Grid>
              {messageFrequency !== "once" && (
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      label={t("message.lastMessageOn")}
                      value={lastMessageOn ? dayjs(lastMessageOn) : null}
                      onChange={(newValue) => {
                        if (newValue && newValue.isValid()) {
                          handleChange("lastMessageOn", newValue.toISOString());
                        } else {
                          handleChange("lastMessageOn", newValue);
                        }
                      }}
                      inputFormat="YYYY-MM-DD, hh:mm A"
                      renderInput={(params) => (
                        <TextField {...params} fullWidth />
                      )}
                      disabled={isReadOnly}
                      minutesStep={10}
                    />
                  </LocalizationProvider>
                </Grid>
              )}
              {showButton && (
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    className="message-modal-submit"
                    fullWidth
                    sx={{
                      borderRadius: "8px",
                      fontWeight: "bold",
                      py: 1.5,
                      mt: 2,
                      background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
                      color: "#fff",
                      boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
                      "&:hover": { background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)" }
                    }}
                  >
                    {isReadOnly
                      ? t("message.clone")
                      : !isCloning && message
                        ? t("message.update")
                        : t("message.submit")}
                  </Button>
                </Grid>
              )}
            </Grid>
          </form>
          <input
            type="file"
            onChange={handleBulkContactsChange}
            ref={fileInputRef}
            hidden
          />
        </Box>
      </Modal>
      <Plans open={plansModalOpen} onClose={handleClosePlansModal} />
      <ConfirmationModal
        open={groupWarningModalOpen}
        onClose={() => {
          setGroupWarningModalOpen(false);
        }}
        onConfirm={() => {
          setGroupWarningModalOpen(false);
        }}
        title={t("message.whatsAppBlockRisk")}
        message={translatedMessage}
        showCancel={false}
      />
      <ConfirmationModal
        open={deleteLabelModalOpen}
        onClose={() => {
          setLabelToDelete(null);
          setDeleteLabelModalOpen(false);
        }}
        onConfirm={() => {
          handleDeleteLabelConfirmation();
        }}
        title={t("message.deleteLabelTitle")}
        message={t("message.deleteLabelMessage")}
        showCancel={true}
      />
    </Box>
  );
}
